import * as Sentry from '@sentry/react';
import MarketingTouchApi from '_services/Api/MarketingTouch';
import { getOrCreateAnonId } from './AnonIdService';

const UTM_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_placement',
    'utm_campaignid',
    'utm_adgroupid',
    'utm_creative',
] as const;

type UtmField = (typeof UTM_FIELDS)[number];
type UtmMap = Partial<Record<UtmField, string>>;

const DEDUP_SIG_KEY = 'kn_last_touch_sig';
const MAX_PATH_LEN = 500;

const readUtms = (search: string): UtmMap => {
    const params = new URLSearchParams(search);
    const out: UtmMap = {};
    for (const field of UTM_FIELDS) {
        const v = params.get(field);
        if (v) out[field] = v;
    }
    return out;
};

const hasAnyUtm = (utms: UtmMap): boolean => UTM_FIELDS.some((f) => !!utms[f]);

const truncate = (s: string, max: number): string =>
    s.length > max ? s.slice(0, max) : s;

// Allowlist for landing_path query — only UTMs are preserved. Anything else
// (?email=…, ?token=…, etc.) is dropped so we never forward PII or secrets.
const keepOnlyUtms = (search: string): string => {
    if (!search) return '';
    const params = new URLSearchParams(search);
    const next = new URLSearchParams();
    for (const field of UTM_FIELDS) {
        const v = params.get(field);
        if (v) next.append(field, v);
    }
    const out = next.toString();
    return out ? `?${out}` : '';
};

const buildLandingPath = (): string => {
    const path =
        window.location.pathname + keepOnlyUtms(window.location.search);
    return truncate(path, MAX_PATH_LEN);
};

const buildReferrer = (): string | undefined => {
    const r = document.referrer;
    return r ? truncate(r, MAX_PATH_LEN) : undefined;
};

const sha1Hex = async (input: string): Promise<string> => {
    // crypto.subtle is gated to secure contexts. Outside HTTPS/localhost it's
    // undefined; we just need a stable dedup key, not a cryptographic digest,
    // so fall back to the raw input rather than dropping the touch entirely.
    if (!globalThis.crypto?.subtle) return input;
    try {
        const buf = new TextEncoder().encode(input);
        const digest = await crypto.subtle.digest('SHA-1', buf);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return input;
    }
};

const signature = (anonId: string, utms: UtmMap): Promise<string> =>
    sha1Hex(
        [
            anonId,
            utms.utm_source ?? '',
            utms.utm_campaign ?? '',
            utms.utm_content ?? '',
            utms.utm_campaignid ?? '',
        ].join('|'),
    );

const readLastSig = (): string | null => {
    try {
        return window.sessionStorage.getItem(DEDUP_SIG_KEY);
    } catch {
        return null;
    }
};

const writeLastSig = (sig: string): void => {
    try {
        window.sessionStorage.setItem(DEDUP_SIG_KEY, sig);
    } catch {
        // sessionStorage blocked — no dedup, accept the duplicate risk
    }
};

// In-memory guard against concurrent calls within the same document load.
// React 18 StrictMode runs effects twice in dev; without this guard, the
// second invocation can fire before the first await resolves and miss the
// stored sig. Sessionstorage still backs cross-mount dedup.
let inFlight: Promise<void> | null = null;

/**
 * Fires the marketing touch POST on initial landing if UTMs are present.
 * Fire-and-forget: errors are swallowed (logged as Sentry breadcrumbs) so
 * tracking failures never affect the user's experience.
 *
 * Call once per document load, not on SPA route changes.
 */
export const captureLandingTouch = async (): Promise<void> => {
    if (inFlight) return inFlight;
    inFlight = (async () => {
        try {
            /**
             * Mint/refresh anon_id on every landing — spec requires the cookie
             * TTL to slide on each visit so long-time organic users don't
             * expire out of the attribution chain. UTM-less visits still need
             * a stable id ready for whenever the user signs up.
             */
            const anonId = getOrCreateAnonId();

            const utms = readUtms(window.location.search);
            if (!hasAnyUtm(utms)) return;
            if (!anonId) return;

            const sig = await signature(anonId, utms);
            if (readLastSig() === sig) return;

            const payload = {
                anon_id: anonId,
                ...utms,
                landing_path: buildLandingPath(),
                referrer: buildReferrer(),
            };

            await MarketingTouchApi.send(payload);
            writeLastSig(sig);
        } catch (error) {
            Sentry.addBreadcrumb({
                category: 'marketing_touch',
                level: 'warning',
                message: 'captureLandingTouch failed',
                data: { error: String(error) },
            });
        }
    })();
    return inFlight;
};
